import * as APIHelper from "services/api"
import * as endPoint from "shared/constants/endpoint"
import DeliveryOrder from "shared/models/delivery-order"
import ProductItem from "shared/models/product-item"
import { detailsKeys, reorderedKeys } from "pages/sale-order/constants"
import { format } from "date-fns"
import { parseDate } from "utils/date-helper"
import { GlobalData } from "shared/global-data"

class SaleOrderBusiness {
  getData = async searchParams => {
    const options = { params: searchParams }
    const response = (await APIHelper.getRequest(`${endPoint.salesOrderSearch.list}`, options)) || {}
    return response?.resource || []
  }

  getDeliveryInformation = async params => {
    const options = { params }
    const response = await APIHelper.getRequest(`${endPoint.shipment.getDeliveryInformation}`, options)
    let result = Array.isArray(response?.resource) && response.resource.length > 0 ? response.resource[0] : null
    if (result?.details) {
      result.details = result.details.map(x => {
        return { ...x, qty: x.quantity, price: x.item_price, validation: {} }
      })
    }
    return result
  }

  headerMapping = obj => {
    return new DeliveryOrder(obj)
  }

  isBOMItem = productVal => {
    productVal = (productVal || "").toUpperCase()
    const product = (GlobalData.ProductListSelection || []).find(x => x.id.toUpperCase() === productVal)
    return product?.item_type === "B"
  }

  productNewDelete = (products, deletedProducts, oldProducts) => {
    const list = []

    const productsNew = products?.filter(item => item.action === "NEW" && !item.parentProduct)

    if (productsNew) {
      productsNew.forEach((element, idx) => {
        const existProduct = oldProducts?.find(item => item.line_num === element.line_num)

        const item = new ProductItem(element, existProduct ? "CHANGE" : "NEW", this.isBOMItem(element.product))
        list.push(item)
      })
    }

    if (deletedProducts) {
      deletedProducts.forEach(element => {
        const existProduct = productsNew.find(item => item.line_num === element.line_num)
        if (!existProduct) {
          const item = new ProductItem(element, "DELETE")
          list.push(item)
        }
      })
    }

    return list
  }

  productChange = (products, oldProducts) => {
    const list = []
    const productList = products?.filter(item => item.action === "CHANGE" && !item.parentProduct)
    if (!productList) {
      return list
    }

    productList.forEach((element, idx) => {
      const oldProduct = oldProducts?.find(item => item.line_id === element.line_id)

      if (oldProduct && Number.parseFloat(element.line_num) === Number.parseFloat(oldProduct.line_num)) {
        const item = new ProductItem(element, "CHANGE", this.isBOMItem(element.product))
        list.push(item)
      } else {
        if (oldProduct) {
          const itemDelete = new ProductItem(oldProduct, "DELETE")
          list.push(itemDelete)
        }

        const itemNew = new ProductItem(element, "NEW", this.isBOMItem(element.product))
        list.push(itemNew)
      }
    })

    return list
  }

  addData = async params => {
    const itemPayload = this.headerMapping(params)
    const detailItems = []
    params.products?.forEach(element => {
      if (!element.parentProduct) {
        const item = new ProductItem(element, "NEW")
        detailItems.push(item)
      }
    })

    itemPayload.details = detailItems
    const payload = { shipments: [itemPayload] }

    return await APIHelper.postRequest(`${endPoint.shipment.post}`, payload)
  }

  updateData = async params => {
    const itemPayload = this.headerMapping(params)
    // make sure line_num of products are in the same format
    params.products?.forEach(item => {
      item["line_num"] = Number(item["line_num"]).toFixed(3)
    })
    params.details?.forEach(item => {
      item["line_num"] = Number(item["line_num"]).toFixed(3)
    })

    const deletedProducts = params.products?.filter(x => x.action == "DELETE")

    const productNewDelete = this.productNewDelete(params.products, deletedProducts, params.details)
    const productChange = this.productChange(params.products, params.details)

    itemPayload.details = [...productNewDelete, ...productChange]
    const payload = { shipments: [itemPayload] }

    return await APIHelper.patchRequest(`${endPoint.shipment.put}`, payload)
  }

  deleteData = async params => {
    return await APIHelper.deleteRequest(`${endPoint.shipment.delete}`, params)
  }

  batchDelete = async params => {
    return await APIHelper.deleteRequest(`${endPoint.shipment.batchDelete}`, params)
  }

  getUploadModel = data => {
    const reorderedData = {}
    const reorderedDetail = {}
    reorderedKeys.forEach(item => {
      reorderedData[item] = data[item] || ""
    })

    reorderedData["details"] = []
    detailsKeys.forEach(item => {
      if (item == "quantity" || item == "net_price") {
        reorderedDetail[item] = !data[item] ? 0 : parseFloat(data[item])
      } else {
        reorderedDetail[item] = data[item]
      }
    })
    reorderedDetail["action"] = "NEW"

    reorderedData["details"].push(reorderedDetail)
    reorderedData["details"][0]["erp_order_line_num"] = 1
    reorderedData["details"][0]["quantity_um"] = "EA"

    if (!reorderedData["scheduled_ship_date"]) {
      delete reorderedData["scheduled_ship_date"]
    } else {
      reorderedData["scheduled_ship_date"] = format(parseDate(reorderedData["scheduled_ship_date"]), "yyyy-MM-dd")
    }
    return reorderedData
  }

  getCombineData = addData => {
    let combineData = [...addData]
    let sameDataGroupIdx = []
    let detailsData = []
    let sameData = []
    let sameDataIdx = []
    combineData.forEach((item, id) => {
      sameData[id] = []
      reorderedKeys.forEach((key, i) => {
        if (key !== "details") {
          sameData[id][i] = false
        }
      })
    })
    combineData.forEach((item, id) => {
      reorderedKeys.forEach((key, index) => {
        combineData.forEach((value, idx) => {
          if (key !== "details") {
            if (idx !== id) {
              if (item[key] === value[key]) {
                sameData[id][index] = true
              }
            }
          }
        })
      })
    })
    sameData.forEach((item, id) => {
      sameDataIdx.push(id)
    })
    sameDataIdx.forEach((item, id) => {
      let groupIndex = [item]
      sameDataIdx.forEach((value, idx) => {
        if (value > item) {
          if (combineData[item].erp_order === combineData[value].erp_order && combineData[item].ship_to_country === combineData[value].ship_to_country) {
            groupIndex.push(value)
          }
        }
      })
      sameDataGroupIdx.push(groupIndex)
    })

    for (var nn = sameDataGroupIdx.length - 1; nn >= 0; nn--) {
      if (nn !== 0) {
        if (sameDataGroupIdx[nn - 1].includes(sameDataGroupIdx[nn][0])) {
          sameDataGroupIdx.splice(nn, 1)
        }
      }
    }
    sameDataGroupIdx.forEach((item, idx) => {
      let detailsGroup = []
      item.forEach((val, i) => {
        detailsGroup.push(combineData[val].details[0])
        detailsData[idx] = detailsGroup
      })
    })
    detailsData.forEach((item, idx) => {
      item.forEach((value, i) => {
        detailsData[idx][i].erp_order_line_num = i + 1
        // value.erp_order_line_num = i+1
      })
    })

    sameDataGroupIdx.forEach((item, idx) => {
      item.forEach((value, id) => {
        if (id === 0) {
          if (combineData[value]) {
            combineData[value].details = detailsData[idx]
          }
        }
        if (id !== 0) {
          combineData[value] = null
        }
      })
    })

    combineData.forEach((item, idx) => {
      if (item && item.details) {
        let details = [...item.details]
        details.forEach((value, index) => {
          if (item.erp_order !== details[index].erp_order) {
            details[index] = null
          } else {
            details[index].erp_order_line_num = index + 1
          }
        })
        let combineDetails = [...details]
        details.forEach((value, index) => {
          details.forEach((v, i) => {
            if (v === null) {
              combineDetails.splice(i, 1)
            }
          })
        })

        combineData[idx].details = [...combineDetails]
      }
    })
    for (var i = 0; i < combineData.length; i++) {
      if (combineData[i] === null) {
        combineData.splice(i, 1)
      }
    }
    combineData = combineData.filter(value => value !== null)
    return combineData
  }

  uploadData = async data => {
    const modelData = data.map(x => this.getUploadModel(x))
    let payload = {}
    const combineData = this.getCombineData(modelData)
    if (combineData) {
      payload = {
        shipments: combineData,
      }
    } else {
      payload = {
        shipments: modelData,
      }
    }

    return await APIHelper.postRequest(`${endPoint.shipment.post}`, payload)
  }

  setOnOrOffHold = async params => {
    const options = { params }
    return await APIHelper.getRequest(`${endPoint.shipment.setOnOrOffHold}`, options)
  }
}

export const saleOrderBusiness = new SaleOrderBusiness()
